import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  AlertTriangle,
  Wrench,
  Droplets,
  Ban,
  Zap,
  Phone,
  Monitor,
  Smartphone,
} from "lucide-react";

const DEFAULT_ISSUE_TYPES = [
  {
    id: "accidental_damage",
    label: "Accidental Damage",
    icon: "AlertTriangle",
    aiContext:
      "Customer has experienced accidental damage to their device. Focus on understanding the circumstances of the accident, extent of damage, and whether the device is still functional. Ask for photos showing the damage and request details about when and how the accident occurred.",
  },
  {
    id: "hardware_defect",
    label: "Hardware Defect",
    icon: "Wrench",
    aiContext:
      "Customer is reporting a hardware malfunction or defect. Focus on troubleshooting the specific symptoms, when the issue started, and what troubleshooting steps they've already tried. Determine if this is a manufacturing defect or normal wear and tear.",
  },
  {
    id: "liquid_damage",
    label: "Liquid Damage",
    icon: "Droplets",
    aiContext:
      "Customer has experienced liquid damage to their device. Focus on the type of liquid, how much contact occurred, immediate actions taken, and current device functionality. Ask for photos and determine if the device was powered on when liquid contact occurred.",
  },
  {
    id: "complete_failure",
    label: "Complete Failure",
    icon: "Ban",
    aiContext:
      "Customer's device has completely stopped functioning. Focus on understanding what happened leading up to the failure, any error messages, charging behavior, and physical condition. Determine if this is a sudden failure or gradual degradation.",
  },
];

const ICON_MAP = {
  AlertTriangle,
  Wrench,
  Droplets,
  Ban,
  Zap,
  Phone,
  Monitor,
  Smartphone,
};

export default function IssueTypeSelector({
  planId,
  issueTypes,
  onIssueTypeSelected,
}) {
  const [selectedIssue, setSelectedIssue] = useState(null);

  const availableIssueTypes = issueTypes || DEFAULT_ISSUE_TYPES;

  const handleIssueSelect = (issueType) => {
    setSelectedIssue(issueType);
  };

  const handleContinue = () => {
    if (selectedIssue) {
      onIssueTypeSelected(selectedIssue);
    }
  };

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || AlertTriangle;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-xl text-center">
          Choose the issue type:
        </CardTitle>
        <p className="text-slate-600 text-center">
          Select the option that best describes your problem
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {availableIssueTypes.map((issueType) => {
            const IconComponent = getIconComponent(issueType.icon);
            const isSelected = selectedIssue?.id === issueType.id;

            return (
              <button
                key={issueType.id}
                onClick={() => handleIssueSelect(issueType)}
                className={`p-6 rounded-xl border-2 transition-all text-center hover:shadow-md ${
                  isSelected
                    ? "border-blue-500 bg-blue-50 shadow-lg"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div
                  className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                    isSelected
                      ? "bg-blue-500 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <IconComponent size={24} />
                </div>
                <h3
                  className={`font-medium text-sm ${
                    isSelected ? "text-blue-900" : "text-slate-900"
                  }`}
                >
                  {issueType.label}
                </h3>
              </button>
            );
          })}
        </div>

        {selectedIssue && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">
              You selected: {selectedIssue.label}
            </h4>
            <p className="text-sm text-blue-800">
              Our AI assistant will help you describe this issue and guide you
              through the claim process.
            </p>
          </div>
        )}

        <div className="flex justify-center">
          <Button
            onClick={handleContinue}
            disabled={!selectedIssue}
            className="bg-blue-600 hover:bg-blue-700 px-8"
          >
            Continue with {selectedIssue?.label || "Selected Issue"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
